import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (b64String) => {
  const padding = '='.repeat((4 - (b64String.length % 4)) % 4);
  const base64 = (b64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

/**
 * Subscribe / Unsubscribe the current browser to backend Web Push (VAPID).
 * Subscriptions are stored server-side and used when a new order arrives —
 * users receive a system notification even when the app/tab is closed.
 */
const PushSubscribeButton = ({ token }) => {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const hasSW = 'serviceWorker' in navigator;
    const hasPM = 'PushManager' in window;
    const hasNotif = 'Notification' in window;
    const ok = hasSW && hasPM && hasNotif;
    setSupported(ok);
    if (!ok || !token) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        setSubscribed(true);
        // Re-sync with backend in case the server lost the record (idempotent upsert)
        try {
          await axios.post(`${API}/push/subscribe`, sub.toJSON(), {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          console.warn('Push resync to backend failed', e);
        }
      } else {
        setSubscribed(false);
      }
    }).catch(() => {});
  }, [token]);

  // Detect environment for clearer error messages
  const ua = (typeof window !== 'undefined' && window.navigator.userAgent) || '';
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isStandalone = (typeof window !== 'undefined') && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  const subscribe = async () => {
    setBusy(true);
    try {
      // iOS Safari: Web Push requires the app to be installed as PWA & opened in standalone mode
      if (isIos && !isStandalone) {
        toast.error(
          'iPhone\'da bildirim almak için: Safari\'de Paylaş > "Ana Ekrana Ekle" yapın, sonra ana ekrandaki Central simgesinden açıp tekrar deneyin.',
          { duration: 9000 }
        );
        return;
      }
      // 1) Notification permission
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error('Bildirim izni reddedildi. Tarayıcı ayarlarından bu site için bildirimleri açın.');
        return;
      }
      // 2) Get VAPID public key from backend
      const { data } = await axios.get(`${API}/push/vapid-key`);
      if (!data?.publicKey) {
        toast.error('Sunucudan VAPID anahtarı alınamadı');
        return;
      }
      const appKey = urlBase64ToUint8Array(data.publicKey);
      // 3) Subscribe via service worker
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appKey
      });
      // 4) Send to backend
      await axios.post(`${API}/push/subscribe`, sub.toJSON(), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribed(true);
      toast.success('Bildirimler aktif — uygulama kapalıyken de sipariş bildirimi gelir');
    } catch (e) {
      console.error('push subscribe failed', e);
      const msg = e?.message || '';
      if (msg.includes('not supported')) {
        toast.error('Bu tarayıcı Web Push desteklemiyor');
      } else if (msg.includes('denied') || msg.includes('NotAllowedError')) {
        toast.error('Bildirim izni reddedildi. Tarayıcı ayarlarından açın.');
      } else {
        toast.error(`Bildirim aktivasyonu başarısız: ${msg || 'Bilinmeyen hata'}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await axios.delete(`${API}/push/subscribe`, {
            headers: { Authorization: `Bearer ${token}` },
            data: sub.toJSON(),
          });
        } catch {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.info('Bildirimler kapatıldı');
    } catch {
      toast.error('İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await axios.post(`${API}/push/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Test bildirimi gönderildi (telefonunuza gelmesi 1-3sn sürebilir)');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      // Backend has no record of this device → auto-resync / fully re-subscribe
      if (e.response?.status === 400) {
        toast.info('Cihaz yeniden kaydediliyor...');
        try {
          const reg = await navigator.serviceWorker.ready;
          let sub = await reg.pushManager.getSubscription();
          // Try simple resync first
          if (sub) {
            try {
              await axios.post(`${API}/push/subscribe`, sub.toJSON(), {
                headers: { Authorization: `Bearer ${token}` }
              });
              await axios.post(`${API}/push/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
              toast.success('Test bildirimi gönderildi (cihaz tekrar kaydedildi)');
              return;
            } catch (resyncErr) {
              console.warn('Simple resync failed, doing full re-subscribe', resyncErr);
              // Force a full re-subscription cycle
              try { await sub.unsubscribe(); } catch {}
            }
          }
          // Full fresh subscribe
          let perm = Notification.permission;
          if (perm === 'default') perm = await Notification.requestPermission();
          if (perm !== 'granted') {
            toast.error('Bildirim izni reddedildi');
            return;
          }
          const { data: vapidData } = await axios.get(`${API}/push/vapid-key`);
          const appKey = urlBase64ToUint8Array(vapidData.publicKey);
          const newSub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appKey
          });
          await axios.post(`${API}/push/subscribe`, newSub.toJSON(), {
            headers: { Authorization: `Bearer ${token}` }
          });
          await axios.post(`${API}/push/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
          setSubscribed(true);
          toast.success('Cihaz yeniden kaydedildi ve test bildirimi gönderildi');
          return;
        } catch (e2) {
          console.error('full re-subscribe failed', e2);
          toast.error(`Yeniden kayıt başarısız: ${e2?.response?.data?.detail || e2?.message || 'bilinmeyen'}`);
          return;
        }
      }
      toast.error(detail || 'Test başarısız');
    }
  };

  if (!supported) {
    // Still show a help pill so the user understands why it doesn't work on their browser
    return (
      <button
        onClick={() => toast.info(
          isIos
            ? 'iPhone\'da bildirim için: Safari > Paylaş > "Ana Ekrana Ekle" yapın, sonra ana ekrandaki Central simgesinden açın.'
            : 'Bu tarayıcı bildirim API\'lerini desteklemiyor. Chrome veya Edge kullanın.',
          { duration: 8000 }
        )}
        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
        data-testid="push-unsupported-btn"
        title="Bildirimler bu tarayıcıda desteklenmiyor"
      >
        <BellOff className="w-3.5 h-3.5" />
        <span className="hidden md:inline">Bildirim Yok</span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5" data-testid="push-subscribe-controls">
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={busy}
        data-testid="push-subscribe-btn"
        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-60 ${
          subscribed
            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
            : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
        }`}
        title={subscribed ? 'Bildirimleri kapat' : 'Telefonuna sipariş bildirimi yolla'}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : subscribed ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{subscribed ? 'Push Açık' : 'Push Aç'}</span>
      </button>
      {subscribed && (
        <button
          onClick={sendTest}
          data-testid="push-test-btn"
          className="px-2 py-1 rounded-full text-[11px] font-medium bg-stone-700 text-stone-300 hover:bg-stone-600 transition-colors"
          title="Test bildirim gönder"
        >
          Test
        </button>
      )}
    </div>
  );
};

export default PushSubscribeButton;
